import React, { useState, useMemo } from 'react';
import { Calendar, CreditCard, X, AlertCircle } from 'lucide-react';
import lawpayService from '../../services/lawpayService';

function CreatePaymentPlanModal({ invoice, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    plan_name: `Payment Plan - Invoice #${invoice.invoice_number}`,
    number_of_installments: 3,
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    account_type: 'operating',
    auto_charge: false,
    reminder_days_before: 3,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const balanceDue = (invoice.total_amount || 0) - (invoice.paid_amount || 0);

  const installmentPreview = useMemo(() => {
    const num = parseInt(formData.number_of_installments) || 1;
    const baseAmount = Math.floor(balanceDue / num * 100) / 100;
    const remainder = Math.round((balanceDue - baseAmount * num) * 100) / 100;

    const installments = [];
    let currentDate = new Date(formData.start_date + 'T00:00:00');

    for (let i = 0; i < num; i++) {
      const amount = i === num - 1 ? baseAmount + remainder : baseAmount;
      installments.push({
        number: i + 1,
        amount,
        date: new Date(currentDate),
      });

      // Calculate next date
      if (formData.frequency === 'weekly') {
        currentDate.setDate(currentDate.getDate() + 7);
      } else if (formData.frequency === 'bi_weekly') {
        currentDate.setDate(currentDate.getDate() + 14);
      } else {
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }
    return installments;
  }, [formData.number_of_installments, formData.frequency, formData.start_date, balanceDue]);

  const formatCurrency = (amount) =>
    `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      await lawpayService.createPaymentPlan({
        client_id: invoice.client_id,
        invoice_id: invoice.id,
        case_id: invoice.case_id || undefined,
        plan_name: formData.plan_name,
        total_amount: balanceDue,
        number_of_installments: parseInt(formData.number_of_installments),
        frequency: formData.frequency,
        start_date: formData.start_date,
        account_type: formData.account_type,
        auto_charge: formData.auto_charge,
        reminder_days_before: parseInt(formData.reminder_days_before),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create payment plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Calendar className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Set Up Payment Plan</h2>
              <p className="text-sm text-gray-500">Invoice #{invoice.invoice_number} &middot; Balance: {formatCurrency(balanceDue)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Plan Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
              <input
                type="text"
                value={formData.plan_name}
                onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Number of Installments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Installments</label>
                <input
                  type="number"
                  min="2"
                  max="60"
                  value={formData.number_of_installments}
                  onChange={(e) => setFormData({ ...formData, number_of_installments: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi_weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <select
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-yellow-500 focus:border-yellow-500"
                >
                  <option value="operating">Operating</option>
                  <option value="trust">Trust</option>
                </select>
              </div>
            </div>

            {/* Auto Charge */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_charge"
                checked={formData.auto_charge}
                onChange={(e) => setFormData({ ...formData, auto_charge: e.target.checked })}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label htmlFor="auto_charge" className="text-sm text-gray-700">
                Auto-charge when installments are due
              </label>
            </div>

            {/* Installment Preview */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Installment Schedule Preview</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">#</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Due Date</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installmentPreview.map((inst) => (
                      <tr key={inst.number} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-2 px-3 text-gray-600">{inst.number}</td>
                        <td className="py-2 px-3 text-gray-900">{inst.date.toLocaleDateString()}</td>
                        <td className="py-2 px-3 text-right font-medium text-gray-900">{formatCurrency(inst.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan="2" className="py-2 px-3 font-semibold text-gray-900">Total</td>
                      <td className="py-2 px-3 text-right font-bold text-gray-900">{formatCurrency(balanceDue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || balanceDue <= 0}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50 flex items-center gap-2"
            >
              <CreditCard className="h-4 w-4" />
              {saving ? 'Creating...' : 'Create Payment Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreatePaymentPlanModal;
